import { Button, Checkbox, Input, Spin, Typography } from 'antd';
import { useMemo, useState } from 'react';

import { useAllBranches } from '@/hooks/queries/admin';

const { Text } = Typography;

type BranchTransferProps = {
  value?: number[];
  onChange?: (value: number[]) => void;
  disabled?: boolean;
};

const BranchTransfer = ({
  value = [],
  onChange,
  disabled = false,
}: BranchTransferProps) => {
  const { data: branches = [], isFetching } = useAllBranches();
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [leftChecked, setLeftChecked] = useState<number[]>([]);
  const [rightChecked, setRightChecked] = useState<number[]>([]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const leftBranches = useMemo(
    () => branches.filter((b) => !selectedSet.has(b.id)),
    [branches, selectedSet]
  );
  const rightBranches = useMemo(
    () => branches.filter((b) => selectedSet.has(b.id)),
    [branches, selectedSet]
  );

  const filteredLeft = useMemo(
    () =>
      leftBranches.filter((b) =>
        b.branchName.toLowerCase().includes(leftSearch.toLowerCase())
      ),
    [leftBranches, leftSearch]
  );
  const filteredRight = useMemo(
    () =>
      rightBranches.filter((b) =>
        b.branchName.toLowerCase().includes(rightSearch.toLowerCase())
      ),
    [rightBranches, rightSearch]
  );

  const leftCheckedInFiltered = leftChecked.filter((id) =>
    filteredLeft.some((b) => b.id === id)
  );
  const rightCheckedInFiltered = rightChecked.filter((id) =>
    filteredRight.some((b) => b.id === id)
  );

  const isAllLeftChecked =
    filteredLeft.length > 0 &&
    filteredLeft.every((b) => leftChecked.includes(b.id));
  const isAllRightChecked =
    filteredRight.length > 0 &&
    filteredRight.every((b) => rightChecked.includes(b.id));

  const handleSelectAllLeft = (checked: boolean) => {
    if (checked) {
      const newIds = filteredLeft.map((b) => b.id);
      setLeftChecked((prev) =>
        Array.from(new Set([...prev, ...newIds]))
      );
    } else {
      const filteredIds = new Set(filteredLeft.map((b) => b.id));
      setLeftChecked((prev) => prev.filter((id) => !filteredIds.has(id)));
    }
  };

  const handleSelectAllRight = (checked: boolean) => {
    if (checked) {
      const newIds = filteredRight.map((b) => b.id);
      setRightChecked((prev) =>
        Array.from(new Set([...prev, ...newIds]))
      );
    } else {
      const filteredIds = new Set(filteredRight.map((b) => b.id));
      setRightChecked((prev) => prev.filter((id) => !filteredIds.has(id)));
    }
  };

  const handleLeftCheck = (branchId: number, checked: boolean) => {
    setLeftChecked((prev) =>
      checked ? [...prev, branchId] : prev.filter((id) => id !== branchId)
    );
  };

  const handleRightCheck = (branchId: number, checked: boolean) => {
    setRightChecked((prev) =>
      checked ? [...prev, branchId] : prev.filter((id) => id !== branchId)
    );
  };

  const moveRight = () => {
    onChange?.([...value, ...leftCheckedInFiltered]);
    const movedSet = new Set(leftCheckedInFiltered);
    setLeftChecked((prev) => prev.filter((id) => !movedSet.has(id)));
  };

  const moveLeft = () => {
    const removedSet = new Set(rightCheckedInFiltered);
    onChange?.(value.filter((id) => !removedSet.has(id)));
    setRightChecked((prev) => prev.filter((id) => !removedSet.has(id)));
  };

  const panelStyle: React.CSSProperties = {
    flex: 1,
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 300,
    maxHeight: 400,
  };

  const panelHeaderStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderBottom: '1px solid #d9d9d9',
    background: '#fafafa',
    borderRadius: '6px 6px 0 0',
  };

  const listStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
  };

  const itemStyle: React.CSSProperties = {
    padding: '4px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    userSelect: 'none',
  };

  if (isFetching) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'stretch',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : undefined,
      }}
    >
      {/* Left Panel */}
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong>전체 지점 {leftBranches.length}개</Text>
            <Checkbox
              checked={isAllLeftChecked}
              disabled={disabled}
              indeterminate={
                leftCheckedInFiltered.length > 0 && !isAllLeftChecked
              }
              onChange={(e) => handleSelectAllLeft(e.target.checked)}
            >
              전체 선택
            </Checkbox>
          </div>
          <Input
            placeholder="지점명을 검색해주세요"
            value={leftSearch}
            onChange={(e) => setLeftSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            size="small"
            disabled={disabled}
          />
        </div>
        <div style={listStyle}>
          {filteredLeft.map((branch) => (
            <div
              key={branch.id}
              role="option"
              aria-selected={leftChecked.includes(branch.id)}
              tabIndex={disabled ? -1 : 0}
              style={itemStyle}
              onClick={() =>
                !disabled &&
                handleLeftCheck(branch.id, !leftChecked.includes(branch.id))
              }
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleLeftCheck(
                    branch.id,
                    !leftChecked.includes(branch.id)
                  );
                }
              }}
            >
              <Checkbox
                checked={leftChecked.includes(branch.id)}
                disabled={disabled}
                onChange={(e) => handleLeftCheck(branch.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <span>{branch.branchName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Arrow buttons */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <Button
          size="small"
          onClick={moveRight}
          disabled={disabled || leftCheckedInFiltered.length === 0}
        >
          &gt;
        </Button>
        <Button
          size="small"
          onClick={moveLeft}
          disabled={disabled || rightCheckedInFiltered.length === 0}
        >
          &lt;
        </Button>
      </div>

      {/* Right Panel */}
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong>적용 지점 {rightBranches.length}개</Text>
            <Checkbox
              checked={isAllRightChecked}
              disabled={disabled}
              indeterminate={
                rightCheckedInFiltered.length > 0 && !isAllRightChecked
              }
              onChange={(e) => handleSelectAllRight(e.target.checked)}
            >
              전체 선택
            </Checkbox>
          </div>
          <Input
            placeholder="지점명을 검색해주세요"
            value={rightSearch}
            onChange={(e) => setRightSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            size="small"
            disabled={disabled}
          />
        </div>
        {value.length === 0 && (
          <div
            style={{
              padding: '12px',
              textAlign: 'center',
              color: '#1677FF',
              fontSize: 12,
              borderBottom: '1px solid #d9d9d9',
            }}
          >
            선택된 지점이 없으면 전체 공지로 등록됩니다.
          </div>
        )}
        <div style={listStyle}>
          {filteredRight.map((branch) => (
            <div
              key={branch.id}
              role="option"
              aria-selected={rightChecked.includes(branch.id)}
              tabIndex={disabled ? -1 : 0}
              style={itemStyle}
              onClick={() =>
                !disabled &&
                handleRightCheck(branch.id, !rightChecked.includes(branch.id))
              }
              onKeyDown={(e) => {
                if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleRightCheck(
                    branch.id,
                    !rightChecked.includes(branch.id)
                  );
                }
              }}
            >
              <Checkbox
                checked={rightChecked.includes(branch.id)}
                disabled={disabled}
                onChange={(e) => handleRightCheck(branch.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <span>{branch.branchName}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BranchTransfer;
